import type { CSSProperties, ReactNode } from 'react';
import { cn } from './cn';

export interface CodeChipProps {
  children: ReactNode;
  /** An identity colour (e.g. `entityHue(code)`) — omit for a neutral chip. */
  hue?: string;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

/**
 * AN IDENTIFIER, DRAWN AS ONE — a warehouse code, a batch number, a document number.
 *
 * These are the strings people scan for, and they are usually printed beside a human-readable fact
 * ("WH-MUM · Mumbai Warehouse", "AGN-1L-OPENING-379 · exp 31 Dec 2026"). Joined by a dot the two read
 * as a single run-on line and the identifier — the half being looked for — dissolves into it. Given a
 * shape of its own it becomes a column the eye can run down, and the fact beside it stays a fact.
 *
 * Tabular figures + a slight letter-spacing so digit strings line up and 0/O stay apart, without the
 * full typewriter look of a monospace stack.
 */
export function CodeChip({ children, hue, className, style, title }: CodeChipProps) {
  return (
    <span
      title={title}
      className={cn(
        'inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap rounded-control px-1.5 py-px type-caption-strong leading-tight tabular-nums tracking-[0.02em]',
        !hue && 'bg-surface-3 text-muted shadow-[inset_0_0_0_1px_var(--gb-border)]',
        className,
      )}
      style={hue
        ? {
            background: `color-mix(in srgb, ${hue} 16%, transparent)`,
            boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${hue} 40%, transparent)`,
            color: hue,
            ...style,
          }
        : style}
    >
      {children}
    </span>
  );
}
